import { useEffect, useMemo, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { HeroPhoto } from '@/components/dashboard/HeroPhoto';
import type { PhotoDTO } from '@/types/db';
import { cn } from '@/lib/utils';

export interface HeroGalleryProps {
  photos: PhotoDTO[];
  propertyId: number;
  /** Used as alt-text fallback when a photo's `alt` is empty. */
  addressLabel: string;
  /**
   * Surfaces favorite-toggle errors to the route shell (Plan 03 wires a
   * toast). Optional — when omitted, errors are swallowed silently after
   * the optimistic state is rolled back.
   */
  onFavoriteError?: (message: string) => void;
  className?: string;
}

const SWIPE_THRESHOLD_PX = 50;

/**
 * Human-readable source label for per-photo provenance badge (Plan 11-05).
 * NULL → "Zillow" (back-compat: ~239 existing rows have no source set).
 * "streetview" → "Street View" (canonical Tier 0 curbside label).
 * Others: capitalise the first letter (e.g. "realtor" → "Realtor").
 */
function sourceLabel(source: string | null): string {
  if (!source) return 'Zillow';
  if (source === 'streetview') return 'Street View';
  return source.charAt(0).toUpperCase() + source.slice(1);
}

/**
 * `<HeroGallery>` — main photo + thumbnail strip (md+) / pagination dots
 * (mobile) + favorite-heart toggle. Per UI-SPEC § 3.1.
 *
 * State:
 * - `activeIndex` — which photo is the main image (default: first favorite,
 *   else 0).
 * - `optimisticFavorites` — instant heart-toggle UI before API confirms.
 *
 * Keyboard (UI-SPEC § 3.1):
 * - ArrowLeft/ArrowRight cycle activeIndex (preventDefault).
 * - Home/End jump to first/last.
 * - Space/Enter on heart toggles favorite.
 *
 * Heart click flow:
 * - Optimistic toggle of `optimisticFavorites[photo.id]`.
 * - POST to `/api/properties/:id/images/:imageId/favorite` (existing v1.0
 *   endpoint).
 * - On error: rollback + invoke `onFavoriteError`.
 *
 * Empty state (`photos.length === 0`): renders `<HeroPhoto>` broken-image
 * fallback with caption "No photos available". No thumbnail strip. No heart.
 *
 * Mobile (<md): thumbnail strip hidden; pagination dots replace it.
 * Touch swipe: pointerdown/up with deltaX > 50px threshold.
 *
 * ARIA:
 * - Outer: `role="region" aria-roledescription="carousel"`.
 * - Heart: `aria-label` reflects current state; `aria-pressed`.
 * - Thumbnails: `aria-label="View photo N of total"`; `aria-current="true"`.
 * - Pagination dots: `aria-label="Go to photo N"`.
 */
export function HeroGallery({
  photos,
  propertyId,
  addressLabel,
  onFavoriteError,
  className,
}: HeroGalleryProps) {
  // Initial active index: first favorite if any, else 0.
  const initialActive = useMemo(() => {
    const firstFav = photos.findIndex((p) => p.is_favorite);
    return firstFav >= 0 ? firstFav : 0;
  }, [photos]);

  const [activeIndex, setActiveIndex] = useState<number>(initialActive);
  const [optimisticFavorites, setOptimisticFavorites] = useState<
    Record<number, boolean>
  >({});

  // Reset active index if the photo list changes shape (e.g. delete/reload).
  useEffect(() => {
    if (activeIndex >= photos.length) {
      setActiveIndex(0);
    }
  }, [photos.length, activeIndex]);

  const pointerStartXRef = useRef<number | null>(null);

  // ---- Empty state ---------------------------------------------------
  if (photos.length === 0) {
    return (
      <GlassPanel
        variant="base"
        role="region"
        aria-label="Property photo gallery"
        className={cn('overflow-hidden p-0', className)}
      >
        <div className="aspect-video relative">
          <HeroPhoto src={null} alt={addressLabel} priority />
        </div>
        <p className="text-sm text-foam-50/60 text-center py-3">
          No photos available
        </p>
      </GlassPanel>
    );
  }

  const total = photos.length;
  const safeIndex = Math.min(Math.max(0, activeIndex), total - 1);
  const activePhoto = photos[safeIndex];
  if (!activePhoto) {
    return null;
  }

  const isFavorited =
    optimisticFavorites[activePhoto.id] ?? activePhoto.is_favorite;

  // ---- Handlers ------------------------------------------------------
  const cycle = (delta: number) => {
    setActiveIndex((i) => (i + delta + total) % total);
  };

  const jumpTo = (i: number) => {
    if (i < 0 || i >= total) return;
    setActiveIndex(i);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        cycle(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        cycle(1);
        break;
      case 'Home':
        e.preventDefault();
        jumpTo(0);
        break;
      case 'End':
        e.preventDefault();
        jumpTo(total - 1);
        break;
      default:
        break;
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    pointerStartXRef.current = e.clientX;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartXRef.current;
    pointerStartXRef.current = null;
    if (start == null) return;
    const delta = e.clientX - start;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta < 0) cycle(1);
    else cycle(-1);
  };

  const toggleFavorite = async (photoId: number) => {
    const photo = photos.find((p) => p.id === photoId);
    if (!photo) return;
    const previous =
      optimisticFavorites[photoId] !== undefined
        ? optimisticFavorites[photoId]
        : photo.is_favorite;
    const next = !previous;
    setOptimisticFavorites((prev) => ({ ...prev, [photoId]: next }));

    try {
      const res = await fetch(
        `/api/properties/${propertyId}/images/${photoId}/favorite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch {
      // Rollback optimistic state.
      setOptimisticFavorites((prev) => ({ ...prev, [photoId]: previous }));
      onFavoriteError?.("Couldn't save favorite. Try again.");
    }
  };

  // ---- Render --------------------------------------------------------
  return (
    <GlassPanel
      variant="base"
      role="region"
      aria-label="Property photo gallery"
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn(
        'overflow-hidden p-0',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
        className,
      )}
    >
      {/* Main image area */}
      <div
        className="aspect-video relative bg-ocean-900"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        <img
          key={activePhoto.id}
          src={activePhoto.url}
          alt={activePhoto.alt || `${addressLabel} photo ${safeIndex + 1}`}
          loading="lazy"
          decoding="async"
          width={1280}
          height={720}
          className="w-full h-full object-cover"
        />
        {/* Per-photo source provenance label (Plan 11-05).
            NULL source → "Zillow" (back-compat with ~239 existing rows).
            "streetview" → "Street View".
            Others: capitalise first letter. */}
        <span
          aria-label={`Photo source: ${sourceLabel(activePhoto.source)}`}
          className={cn(
            'absolute bottom-2 left-2',
            'px-2 py-0.5 rounded text-xs font-medium',
            'bg-ocean-950/70 text-foam-50/80 backdrop-blur-sm',
          )}
        >
          {sourceLabel(activePhoto.source)}
        </span>
        {/* Heart toggle */}
        <button
          type="button"
          aria-label={
            isFavorited ? 'Remove from favorites' : 'Mark as favorite'
          }
          aria-pressed={isFavorited}
          onClick={() => void toggleFavorite(activePhoto.id)}
          className={cn(
            'absolute top-3 right-3',
            'w-11 h-11 grid place-items-center rounded-full',
            'bg-ocean-950/60 backdrop-blur-sm border border-foam-50/10',
            'transition-transform duration-100 motion-reduce:transition-none',
            'hover:scale-110 motion-reduce:hover:scale-100',
            'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
            isFavorited ? 'text-coral-500' : 'text-foam-50/70',
          )}
        >
          <Heart
            aria-hidden
            className="w-6 h-6"
            {...(isFavorited ? { fill: 'currentColor' } : {})}
          />
        </button>
      </div>

      {/* Thumbnail strip — md+ only when more than one photo */}
      {total > 1 && (
        <div
          className="hidden md:flex gap-2 overflow-x-auto h-20 p-2 bg-ocean-900/40"
          role="tablist"
          aria-label="Photo thumbnails"
        >
          {photos.map((photo, i) => {
            const isActive = i === safeIndex;
            const photoFav =
              optimisticFavorites[photo.id] ?? photo.is_favorite;
            return (
              <button
                key={photo.id}
                type="button"
                role="tab"
                aria-label={`View photo ${i + 1} of ${total}`}
                aria-current={isActive ? 'true' : undefined}
                aria-selected={isActive}
                onClick={() => jumpTo(i)}
                className={cn(
                  'relative shrink-0 w-24 h-full rounded-lg overflow-hidden',
                  'transition-opacity duration-100 motion-reduce:transition-none',
                  isActive
                    ? 'opacity-100 border-2 border-tide-400'
                    : 'opacity-70 hover:opacity-100 border border-foam-50/10',
                  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
                )}
              >
                <img
                  src={photo.url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                {photoFav && (
                  <span
                    aria-hidden
                    className="absolute bottom-1 right-1 w-4 h-4 grid place-items-center rounded-full bg-ocean-950/70"
                  >
                    <Heart
                      className="w-3 h-3 text-coral-500"
                      fill="currentColor"
                    />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination dots — mobile only when more than one photo */}
      {total > 1 && (
        <nav
          className="md:hidden flex items-center justify-center gap-1 py-2"
          aria-label="Photo navigation"
        >
          {photos.map((photo, i) => {
            const isActive = i === safeIndex;
            return (
              <button
                key={photo.id}
                type="button"
                aria-label={`Go to photo ${i + 1} of ${total}`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => jumpTo(i)}
                className={cn(
                  'p-3 rounded-full',
                  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'block w-2 h-2 rounded-full',
                    isActive ? 'bg-bronze-500' : 'bg-foam-50/30',
                  )}
                />
              </button>
            );
          })}
        </nav>
      )}
    </GlassPanel>
  );
}
